import type { MeetingTranscript } from '@/types/meeting-transcription';

import {
    getTranscriptFileOverLimitMessage,
    TRANSCRIPT_FILE_EMPTY_MESSAGE,
    TRANSCRIPT_FILE_EXTENSIONS,
    TRANSCRIPT_FILE_MIME_TYPES,
    TRANSCRIPT_FILE_TYPE_MESSAGE,
    TRANSCRIPT_INPUT_MAX_FILE_BYTES
} from './constants';

export class TranscriptInputError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'TranscriptInputError';
    }
}

const getFileExtension = (fileName: string): string => {
    const lower = fileName.toLowerCase();
    const dotIndex = lower.lastIndexOf('.');
    return dotIndex >= 0 ? lower.slice(dotIndex) : '';
};

export const isAllowedTranscriptFile = (file: File): boolean => {
    const extension = getFileExtension(file.name);
    if ((TRANSCRIPT_FILE_EXTENSIONS as readonly string[]).includes(extension)) {
        return true;
    }
    return Boolean(file.type && TRANSCRIPT_FILE_MIME_TYPES.has(file.type));
};

/** MIME for upload-url + S3 PUT; must be identical on both steps. */
export const getTranscriptFileContentType = (file: File): string => {
    if (file.type && TRANSCRIPT_FILE_MIME_TYPES.has(file.type)) {
        return file.type;
    }
    switch (getFileExtension(file.name)) {
        case '.vtt':
            return 'text/vtt';
        case '.txt':
            return 'text/plain';
        case '.docx':
            return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        default:
            return file.type || 'application/octet-stream';
    }
};

export const validateTranscriptFile = (file: File): void => {
    if (!isAllowedTranscriptFile(file)) {
        throw new TranscriptInputError(TRANSCRIPT_FILE_TYPE_MESSAGE);
    }
    if (file.size === 0) {
        throw new TranscriptInputError(TRANSCRIPT_FILE_EMPTY_MESSAGE);
    }
    if (file.size > TRANSCRIPT_INPUT_MAX_FILE_BYTES) {
        throw new TranscriptInputError(getTranscriptFileOverLimitMessage());
    }
};

export const buildSingleTranscript = (transcript: MeetingTranscript): MeetingTranscript[] => [transcript];
